import { useEffect, useId, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../../api";
import { useSession } from "../../shell/session";
import type { ApiMeta, Person } from "../../../shared/types";
import { Badge, Dialog, Empty, ErrorNote } from "../../ui";
import "./community.css";
import {
  type Household,
  apiEnvelope,
  canCoordinate,
  errorText,
  initials,
  monogramStyle,
  usePageTitle,
} from "./support";

interface PersonDraft {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  status: "active" | "inactive";
  householdId: string;
  tags: string[];
  instruments: string[];
  roles: string[];
  notes: string;
}

function blankDraft(): PersonDraft {
  return {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    status: "active",
    householdId: "",
    tags: [],
    instruments: [],
    roles: [],
    notes: "",
  };
}

function draftFrom(person: Person): PersonDraft {
  return {
    firstName: person.firstName,
    lastName: person.lastName,
    email: person.email ?? "",
    phone: person.phone ?? "",
    status: person.status === "inactive" ? "inactive" : "active",
    householdId: person.householdId ?? "",
    tags: person.tags,
    instruments: person.instruments,
    roles: person.roles,
    notes: person.notes,
  };
}

function personPayload(draft: PersonDraft) {
  return {
    firstName: draft.firstName.trim(),
    lastName: draft.lastName.trim(),
    email: draft.email.trim() ? draft.email.trim().toLowerCase() : null,
    phone: draft.phone.trim() ? draft.phone.trim() : null,
    status: draft.status,
    householdId: draft.householdId || null,
    tags: draft.tags,
    instruments: draft.instruments,
    roles: draft.roles,
    notes: draft.notes,
  };
}

function validatePerson(draft: PersonDraft): string | null {
  if (!draft.firstName.trim() || !draft.lastName.trim()) return "First and last name are required.";
  if (draft.firstName.trim().length > 80 || draft.lastName.trim().length > 80) return "Names need to be 80 characters or fewer.";
  if (draft.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim())) return "Email needs a name and a domain.";
  if (draft.phone.trim().length > 40) return "Phone needs to be 40 characters or fewer.";
  if (draft.notes.length > 8000) return "Notes are too long.";
  return null;
}

function ListField({ label, value, max, onChange }: { label: string; value: string[]; max: number; onChange: (next: string[]) => void }) {
  const [draft, setDraft] = useState("");
  const [hint, setHint] = useState("");
  const fieldId = useId();
  function add() {
    const next = draft.trim();
    if (!next) return;
    if (next.length > 40) {
      setHint("Keep each one under 40 characters.");
      return;
    }
    if (value.length >= max) {
      setHint(`${max} is the limit.`);
      return;
    }
    setHint("");
    setDraft("");
    if (!value.includes(next)) onChange([...value, next]);
  }
  return (
    <div className="field">
      <label htmlFor={fieldId}>{label}</label>
      <div className="cluster">
        {value.map((item) => (
          <button key={item} className="badge mbc-chip" type="button" onClick={() => onChange(value.filter((entry) => entry !== item))} aria-label={`Remove ${item}`}>
            {item} ×
          </button>
        ))}
      </div>
      <div className="cluster">
        <input id={fieldId} value={draft} onChange={(event) => setDraft(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); add(); } }} placeholder="Type and add" />
        <button className="btn small" type="button" onClick={add}>Add</button>
      </div>
      {hint && <p className="notice">{hint}</p>}
    </div>
  );
}

function PersonForm({
  draft,
  households,
  saving,
  submitLabel,
  onChange,
  onHouseholds,
  onSubmit,
}: {
  draft: PersonDraft;
  households: Household[];
  saving: boolean;
  submitLabel: string;
  onChange: (next: PersonDraft) => void;
  onHouseholds: (next: Household[]) => void;
  onSubmit: () => void;
}) {
  const base = useId();
  const [houseName, setHouseName] = useState("");
  const [houseNotes, setHouseNotes] = useState("");
  const [houseError, setHouseError] = useState("");
  const [houseSaving, setHouseSaving] = useState(false);

  async function addHousehold() {
    const name = houseName.trim();
    if (!name) {
      setHouseError("Name the household.");
      return;
    }
    setHouseSaving(true);
    setHouseError("");
    try {
      const created = await api<Household>("/api/v1/people/households", {
        method: "POST",
        body: JSON.stringify({ name, notes: houseNotes.trim() }),
      });
      onHouseholds([...households, created].sort((left, right) => left.name.localeCompare(right.name)));
      onChange({ ...draft, householdId: created.id });
      setHouseName("");
      setHouseNotes("");
    } catch (caught) {
      setHouseError(errorText(caught));
    } finally {
      setHouseSaving(false);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    onSubmit();
  }

  return (
    <form className="form-grid" onSubmit={submit}>
      <div className="grid-2">
        <div className="field"><label htmlFor={`${base}-first`}>First name</label><input id={`${base}-first`} value={draft.firstName} onChange={(event) => onChange({ ...draft, firstName: event.target.value })} required maxLength={80} /></div>
        <div className="field"><label htmlFor={`${base}-last`}>Last name</label><input id={`${base}-last`} value={draft.lastName} onChange={(event) => onChange({ ...draft, lastName: event.target.value })} required maxLength={80} /></div>
      </div>
      <div className="grid-2">
        <div className="field"><label htmlFor={`${base}-email`}>Email</label><input id={`${base}-email`} type="email" inputMode="email" value={draft.email} onChange={(event) => onChange({ ...draft, email: event.target.value })} autoComplete="off" /></div>
        <div className="field"><label htmlFor={`${base}-phone`}>Phone</label><input id={`${base}-phone`} type="tel" value={draft.phone} onChange={(event) => onChange({ ...draft, phone: event.target.value })} maxLength={40} autoComplete="off" /></div>
      </div>
      <div className="grid-2">
        <div className="field">
          <label htmlFor={`${base}-status`}>Status</label>
          <select id={`${base}-status`} value={draft.status} onChange={(event) => onChange({ ...draft, status: event.target.value === "inactive" ? "inactive" : "active" })}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor={`${base}-house`}>Household</label>
          <select id={`${base}-house`} value={draft.householdId} onChange={(event) => onChange({ ...draft, householdId: event.target.value })}>
            <option value="">No household</option>
            {households.map((house) => <option key={house.id} value={house.id}>{house.name}</option>)}
          </select>
        </div>
      </div>
      <div className="panel panel-pad form-grid">
        <strong>New household</strong>
        <p className="faint">This saves immediately, before the person record.</p>
        <div className="field"><label htmlFor={`${base}-house-name`}>Household name</label><input id={`${base}-house-name`} value={houseName} onChange={(event) => setHouseName(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); void addHousehold(); } }} maxLength={120} /></div>
        <div className="field"><label htmlFor={`${base}-house-notes`}>Household notes</label><input id={`${base}-house-notes`} value={houseNotes} onChange={(event) => setHouseNotes(event.target.value)} maxLength={2000} /></div>
        {houseError && <ErrorNote>{houseError}</ErrorNote>}
        <button className="btn small" type="button" onClick={() => void addHousehold()} disabled={houseSaving}>{houseSaving ? "Saving…" : "Save household"}</button>
      </div>
      <ListField label="Tags" value={draft.tags} max={24} onChange={(tags) => onChange({ ...draft, tags })} />
      <ListField label="Instruments" value={draft.instruments} max={16} onChange={(instruments) => onChange({ ...draft, instruments })} />
      <ListField label="Roles" value={draft.roles} max={16} onChange={(roles) => onChange({ ...draft, roles })} />
      <div className="field"><label htmlFor={`${base}-notes`}>Notes</label><textarea id={`${base}-notes`} value={draft.notes} onChange={(event) => onChange({ ...draft, notes: event.target.value })} maxLength={8000} /></div>
      <button className="btn primary" type="submit" disabled={saving}>{saving ? "Saving…" : submitLabel}</button>
    </form>
  );
}

export function PeoplePage() {
  const { user } = useSession();
  const navigate = useNavigate();
  const canWrite = canCoordinate(user?.role);
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [tag, setTag] = useState("");
  const [instrument, setInstrument] = useState("");
  const [appliedInstrument, setAppliedInstrument] = useState("");
  const [householdFilter, setHouseholdFilter] = useState("");
  const [page, setPage] = useState(1);
  const [people, setPeople] = useState<Person[]>([]);
  const [meta, setMeta] = useState<ApiMeta | null>(null);
  const [households, setHouseholds] = useState<Household[]>([]);
  const [draft, setDraft] = useState<PersonDraft>(blankDraft);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  usePageTitle("People");

  useEffect(() => {
    let live = true;
    setLoading(true);
    const params = new URLSearchParams({ limit: "24", page: String(page) });
    if (submitted) params.set("q", submitted);
    if (tag) params.set("tag", tag);
    if (appliedInstrument) params.set("instrument", appliedInstrument);
    apiEnvelope<Person[]>(`/api/v1/people?${params}`)
      .then((result) => {
        if (!live) return;
        setPeople(result.data);
        setMeta(result.meta);
        setError("");
      })
      .catch((caught: unknown) => { if (live) setError(errorText(caught)); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [submitted, tag, appliedInstrument, page]);

  useEffect(() => {
    void api<Household[]>("/api/v1/people/households/list").then(setHouseholds).catch(() => setHouseholds([]));
  }, []);

  const facetTags = [...new Set(people.flatMap((person) => person.tags))].sort((left, right) => left.localeCompare(right));
  const visible = householdFilter ? people.filter((person) => person.householdId === householdFilter) : people;
  const paged = Boolean(meta && meta.total > people.length);

  async function createPerson() {
    const problem = validatePerson(draft);
    if (problem) {
      setFormError(problem);
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      const created = await api<Person>("/api/v1/people", { method: "POST", body: JSON.stringify(personPayload(draft)) });
      if (!created?.id) throw new Error("The directory did not return the new person.");
      navigate(`/people/${created.id}`);
    } catch (caught) {
      setFormError(errorText(caught));
    } finally {
      setSaving(false);
    }
  }

  async function exportCsv() {
    setError("");
    try {
      const response = await fetch("/api/v1/exports/people.csv", { credentials: "include", headers: { "X-MB-Client": "mblive" } });
      if (!response.ok) throw new Error("Export is available to coordinators.");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "mblive-people.csv";
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (caught) {
      setError(errorText(caught));
    }
  }

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <p className="kicker">Community</p>
          <h1 className="page-title">Directory</h1>
          <p className="lede">Fictional people for Moksha Base. Search the roster, then open a profile to edit tags, instruments, and households.</p>
        </div>
        <div className="cluster">
          {canWrite && <button className="btn" type="button" onClick={() => void exportCsv()}>Export CSV</button>}
          {canWrite && <button className="btn primary" type="button" onClick={() => { setDraft(blankDraft()); setFormError(""); setCreating(true); }}>New person</button>}
        </div>
      </header>
      {!canWrite && <p className="mbc-limit">This login can browse the directory. Adding or editing people needs a coordinator, leader, or owner.</p>}
      <form className="panel panel-pad mbc-filters" onSubmit={(event) => { event.preventDefault(); setPage(1); setSubmitted(query.trim()); setAppliedInstrument(instrument.trim()); }}>
        <div className="field"><label htmlFor="people-q">Search</label><input id="people-q" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Name or tag" autoComplete="off" /></div>
        <div className="field"><label htmlFor="people-instrument">Instrument</label><input id="people-instrument" value={instrument} onChange={(event) => setInstrument(event.target.value)} placeholder="keys, drums" /></div>
        <div className="field">
          <label htmlFor="people-house">Household</label>
          <select id="people-house" value={householdFilter} onChange={(event) => setHouseholdFilter(event.target.value)}>
            <option value="">Any household</option>
            {households.map((house) => <option key={house.id} value={house.id}>{house.name}</option>)}
          </select>
        </div>
        <button className="btn primary" type="submit">Search</button>
      </form>
      {paged && householdFilter && <p className="faint">Household filter looks at this page. Name, tag, and instrument search the full directory.</p>}
      <div className="cluster">
        {facetTags.map((item) => (
          <button key={item} type="button" className="badge mbc-chip" aria-pressed={tag === item} onClick={() => { setTag(tag === item ? "" : item); setPage(1); }}>{item}</button>
        ))}
        {(submitted || tag || appliedInstrument || householdFilter) && (
          <button className="btn ghost small" type="button" onClick={() => { setQuery(""); setSubmitted(""); setTag(""); setInstrument(""); setAppliedInstrument(""); setHouseholdFilter(""); setPage(1); }}>Clear</button>
        )}
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && <p className="muted">Loading the directory…</p>}
      {!loading && visible.length === 0 && <Empty title="No one matches" body="Try another name, tag, or instrument." />}
      <div className="mbc-roster">
        {visible.map((person) => (
          <Link className="mbc-person" key={person.id} to={`/people/${person.id}`}>
            <span className="mbc-mono" style={monogramStyle(person.name)} aria-hidden="true">{initials(person.name)}</span>
            <span>
              <strong>{person.name}</strong>
              <span className="muted" style={{ display: "block" }}>{person.instruments.join(" · ") || "No instrument listed"}</span>
              <span className="faint">{person.householdName ?? "No household"}{person.roles.length > 0 ? ` · ${person.roles.join(", ")}` : ""}</span>
            </span>
            <span className="cluster">
              {person.status !== "active" && <Badge tone="danger">inactive</Badge>}
              {person.tags.slice(0, 3).map((item) => <Badge key={item} tone="violet">{item}</Badge>)}
            </span>
          </Link>
        ))}
      </div>
      {meta && (
        <div className="cluster">
          <button className="btn" type="button" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>Previous</button>
          <span className="muted">Page {meta.page} · {meta.total} people</span>
          <button className="btn" type="button" disabled={page * meta.limit >= meta.total} onClick={() => setPage((current) => current + 1)}>Next</button>
        </div>
      )}
      <p className="faint">Profiles can be marked inactive. Deleting a person is not available.</p>
      {creating && (
        <Dialog title="New person" onClose={() => setCreating(false)}>
          {formError && <ErrorNote>{formError}</ErrorNote>}
          <PersonForm draft={draft} households={households} saving={saving} submitLabel="Create person" onChange={setDraft} onHouseholds={setHouseholds} onSubmit={() => void createPerson()} />
        </Dialog>
      )}
    </section>
  );
}

function ContactBlock({ person, canWrite }: { person: Person; canWrite: boolean }) {
  if (!canWrite) return <p className="mbc-limit">Email, phone, and notes stay with coordinators on this login.</p>;
  return (
    <div className="stack">
      <p>{person.email ?? "No email on file"}</p>
      <p className="muted">{person.phone ?? "No phone on file"}</p>
      {person.notes ? <p className="mbc-body">{person.notes}</p> : <p className="faint">No notes on file.</p>}
    </div>
  );
}

export function PersonPage() {
  const { personId = "" } = useParams();
  const { user } = useSession();
  const canWrite = canCoordinate(user?.role);
  const [person, setPerson] = useState<Person | null>(null);
  const [draft, setDraft] = useState<PersonDraft>(blankDraft);
  const [households, setHouseholds] = useState<Household[]>([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  usePageTitle(person?.name ?? "Person");

  useEffect(() => {
    let live = true;
    setLoading(true);
    setPerson(null);
    Promise.all([
      api<Person>(`/api/v1/people/${personId}`),
      api<Household[]>("/api/v1/people/households/list"),
    ]).then(([next, houses]) => {
      if (!live) return;
      setPerson(next);
      setDraft(draftFrom(next));
      setHouseholds(houses);
      setError("");
    }).catch((caught: unknown) => { if (live) setError(errorText(caught)); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [personId]);

  async function save() {
    const problem = validatePerson(draft);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const saved = await api<Person>(`/api/v1/people/${personId}`, { method: "PATCH", body: JSON.stringify(personPayload(draft)) });
      if (!saved) throw new Error("The profile did not save.");
      setPerson(saved);
      setDraft(draftFrom(saved));
      setNotice("Profile saved.");
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <section className="page">Loading the profile…</section>;
  if (!person) {
    return <section className="page"><ErrorNote>{error || "Person not found"}</ErrorNote><Link to="/people">Back to the directory</Link></section>;
  }

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <Link className="kicker" to="/people">Directory</Link>
          <div className="mbc-identity">
            <span className="mbc-mono" style={monogramStyle(person.name)} aria-hidden="true">{initials(person.name)}</span>
            <div>
              <h1 className="page-title">{person.name}</h1>
              <p className="lede">{person.householdName ?? "No household"} · {person.roles.join(", ") || "No role listed"}</p>
            </div>
          </div>
        </div>
        <Badge tone={person.status === "active" ? "teal" : "danger"}>{person.status}</Badge>
      </header>
      {error && <ErrorNote>{error}</ErrorNote>}
      {notice && <p className="mbc-status" role="status">{notice}</p>}
      <div className="grid-2">
        <article className="panel panel-pad stack">
          <h2>Profile</h2>
          <ContactBlock person={person} canWrite={canWrite} />
          <div className="cluster">{person.tags.map((item) => <Badge key={item} tone="violet">{item}</Badge>)}{person.tags.length === 0 && <span className="faint">No tags</span>}</div>
          <div className="cluster">{person.instruments.map((item) => <Badge key={item} tone="teal">{item}</Badge>)}</div>
          {canWrite ? (
            <PersonForm draft={draft} households={households} saving={saving} submitLabel="Save profile" onChange={setDraft} onHouseholds={setHouseholds} onSubmit={() => void save()} />
          ) : (
            <p className="mbc-limit">Coordinators can edit this profile. Deleting a person is not available.</p>
          )}
        </article>
        <article className="panel panel-pad stack">
          <h2>Groups</h2>
          {person.groups.length === 0 && <p className="muted">No group yet.</p>}
          {person.groups.map((group) => (
            <Link key={group.id} className="list-row" to={`/groups/${group.id}`} style={{ gridTemplateColumns: "1fr auto" }}>
              <strong>{group.name}</strong>
              <Badge>{group.role}</Badge>
            </Link>
          ))}
          <p className="faint">Membership changes happen on the group page.</p>
        </article>
      </div>
    </section>
  );
}
